import { create } from 'zustand';
import {
  loadStoredTrinityPanels,
  reconcileTrinityPanels,
  saveStoredTrinityPanels,
  type TrinityPanelState,
} from '../lib/trinityPanels';
import type { ExposureType, Ticker } from './heatmapStore';

interface WorkspaceStore {
  activeType: ExposureType;
  trinityPanels: TrinityPanelState[];
  setActiveType: (type: ExposureType) => void;
  setTrinityPanelTicker: (panelIndex: number, ticker: Ticker) => void;
  setTrinityPanelType: (panelIndex: number, type: ExposureType) => void;
  reconcileTrinityPanelsWithEnabledTickers: (enabledTickers: readonly Ticker[]) => void;
}

function updatePanel(
  panels: readonly TrinityPanelState[],
  panelIndex: number,
  patch: Partial<TrinityPanelState>,
): TrinityPanelState[] {
  const nextPanels = panels.map((panel, index) =>
    index === panelIndex ? { ...panel, ...patch } : panel,
  );
  saveStoredTrinityPanels(nextPanels);
  return nextPanels;
}

export const useWorkspaceStore = create<WorkspaceStore>((set) => ({
  activeType: 'gex',
  trinityPanels: loadStoredTrinityPanels(),
  setActiveType: (activeType) => set({ activeType }),
  setTrinityPanelTicker: (panelIndex, ticker) =>
    set((state) => ({ trinityPanels: updatePanel(state.trinityPanels, panelIndex, { ticker }) })),
  setTrinityPanelType: (panelIndex, type) =>
    set((state) => ({ trinityPanels: updatePanel(state.trinityPanels, panelIndex, { type }) })),
  reconcileTrinityPanelsWithEnabledTickers: (enabledTickers) =>
    set((state) => {
      if (enabledTickers.length === 0) {
        return {};
      }

      const nextPanels = reconcileTrinityPanels(state.trinityPanels, enabledTickers);
      const panelsChanged = nextPanels.some(
        (panel, index) => panel.ticker !== state.trinityPanels[index]?.ticker,
      );
      if (panelsChanged) {
        saveStoredTrinityPanels(nextPanels);
      }

      return {
        trinityPanels: panelsChanged ? nextPanels : state.trinityPanels,
      };
    }),
}));
